import React, { useEffect, useState } from 'react';
import { Chip, Divider, Modal } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useGlobal } from '@rcg/ui-react';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import FlightLandIcon from '@material-ui/icons/FlightLand';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Img } from 'react-image';
import clsx from 'clsx';
import CloseIcon from '@material-ui/icons/Close';
import { getOverNightIcon } from '../../../utils/icons';
import layoverFormats from '../../../utils/layoverFormats';
import TimeDuration, { timeWithLocale } from '../../../utils/timeFormats';
import useStylesCard from '../../ResultsCard/components/FlightExpanded/useStyles';
import Dictionary from '../../../utils/dictionary';
import { FLIGHT_SEARCH_TYPE } from '../../../utils/constants';
import { ReactComponent as DefaultIcon } from '../../../assets/icons/icon-missing-carrier.svg';
import useStyles from './useStyles';

const TakeOffTime = (props) => {
  const classesExpandCard = useStylesCard();
  const { segment, segmentIndex, flight } = props;
  const {
    data: { localizedDayjs },
  } = useGlobal();
  return (
    <>
      <FlightTakeoffIcon className={classesExpandCard.expandedDepartIcon} />
      {timeWithLocale(localizedDayjs, segment.departure.time)}
      &nbsp;
      {segmentIndex === 0 && <>&nbsp;</>}
      {segmentIndex !== 0 && (
        <>
          <span className={classesExpandCard.layoverDays}>
            {layoverFormats.layoverDays({
              departure: flight.flightSegments[segmentIndex - 1].arrival.date,
              arrival: segment.departure.date,
            })}
          </span>
          &nbsp;
        </>
      )}
    </>
  );
};

const FlightDetailsModal = ({ openModal, handleClose, flightInfo }) => {
  const classes = useStyles();
  const classesExpandCard = useStylesCard();
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(openModal);
  }, [openModal]);

  if (!flightInfo) {
    return <></>;
  }

  const { flight, cabin, flightType } = flightInfo;
  let title = renderContent(
    Dictionary.airDepartingLabel.key,
    Dictionary.airDepartingLabel.defaultValue,
  );
  if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
    title = renderContent(
      Dictionary.airReturningLabel.key,
      Dictionary.airReturningLabel.defaultValue,
    );
  }

  const airportInfo = (segment) => (
    <>
      <FlightLandIcon className={classesExpandCard.expandedDepartIcon} />
      {timeWithLocale(localizedDayjs, segment.arrival.time)}
      <span className={classesExpandCard.layoverDays}>
        &nbsp;
        {layoverFormats.layoverDays({
          arrival: segment.arrival.date,
          departure: flight.flightSegments[0].departure.date,
        })}
      </span>
    </>
  );

  const getCabinClassCode = (segment) => {
    const matchedCabin = segment?.availableCabins?.find(
      ({ selectedCabin }) => selectedCabin === cabin.cabinClass.code,
    );
    return matchedCabin ? matchedCabin.mappedCabin : cabin.cabinClass.code;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="Flight Details Modal"
      aria-describedby="Flight Details Modal"
      className={classes.flightDetailsModal}>
      <div className={classes.flightDetailsBody}>
        <div className={classes.flightDetailsTitle}>
          <Img
            src={flight.flightIcon ? flight.flightIcon : flight.operatingAirlines[0].icon}
            alt=""
            unloader={<DefaultIcon className={classes.multiAirlineLogo} />}
            className={clsx(classesExpandCard.multiAirlineLogo, classes.multiAirlineLogo)}
          />
          <span className={classes.flightDetailsDate}>
            {title} -{' '}
            {localizedDayjs(flight.flightSegments[0].departure.date).format('ddd[,] MMM D[,] YYYY')}
          </span>
          <CloseIcon className={classes.flightDetailsClose} onClick={handleClose} />
        </div>
        {flight.flightSegments.map((segment, segmentIndex) => (
          // eslint-disable-next-line
          <div
            className={clsx(classesExpandCard.expandedItinerary, classes.expandedItineraryModal)}
            key={segment.departure.airport.name}>
            <div className={classesExpandCard.expandedAirportInfo}>
              <Img
                src={segment.marketingAirline.icon}
                alt=""
                className={classesExpandCard.multiAirlineLogo}
                unloader={<DefaultIcon className={classes.multiAirlineLogo} />}
              />
              <p>
                <TakeOffTime segment={segment} segmentIndex={segmentIndex} flight={flight} />
                {`- ${segment.departure.airport.name} (${segment.departure.airport.code})`}
              </p>
            </div>
            <div className={classesExpandCard.expandedAirportInfoSmall}>
              <Img
                src={segment.marketingAirline.icon}
                alt=""
                unloader={<DefaultIcon className={classes.multiAirlineLogo} />}
                className={classesExpandCard.multiAirlineLogo}
              />
              <p>
                <TakeOffTime segment={segment} segmentIndex={segmentIndex} flight={flight} />
              </p>
              <p className={classesExpandCard.airportLabelSmall}>
                {`${segment.departure.airport.city} (${segment.departure.airport.code})`}
              </p>
            </div>
            <div className={classesExpandCard.expandedFlightDuration}>
              <div className={classesExpandCard.expandedDurationIcons}>
                <MoreVertIcon className={classesExpandCard.upperVertical} />
                <MoreVertIcon className={classesExpandCard.lowerVertical} />
              </div>
              <div className={classesExpandCard.expandedDuration}>
                <span className={classesExpandCard.expandedDurationLabel}>
                  {TimeDuration(segment.durationMins)}
                </span>
                {layoverFormats.isOvernightFlight({
                  departure: segment.departure.date,
                  arrival: segment.arrival.date,
                }) && (
                  <Chip
                    className={classesExpandCard.overnightLayover}
                    avatar={getOverNightIcon()}
                    label={renderContent(
                      Dictionary.airOvernightFlightLabel.key,
                      Dictionary.airOvernightFlightLabel.defaultValue,
                    )}
                    variant="outlined"
                  />
                )}
              </div>
            </div>
            <div className={classesExpandCard.expandedAirportInfo}>
              <p>
                {airportInfo(segment)}
                &nbsp;
                {`- ${segment.arrival.airport.name} (${segment.arrival.airport.code})`}
              </p>
            </div>
            <div className={classesExpandCard.expandedAirportInfoSmall}>
              <p>{airportInfo(segment)}</p>
              <p className={classesExpandCard.airportLabelSmall}>
                {`${segment.arrival.airport.city} (${segment.arrival.airport.code})`}
              </p>
            </div>
            <div className={classesExpandCard.expandedFlightInfo}>
              <p>
                {`${renderContent(
                  Dictionary.airOperatorLabel.key,
                  Dictionary.airOperatorLabel.defaultValue,
                )} `}
                {segment.operatingAirline.name}&nbsp;&nbsp;|&nbsp;&nbsp;
                {segment?.airplane?.name}&nbsp;&nbsp;|&nbsp;&nbsp;
                {`${segment.marketingAirline.code} ${segment.number}`}&nbsp;&nbsp;|&nbsp;&nbsp;
                {getCabinClassCode(segment) &&
                  renderContent(
                    Dictionary[`airClass${getCabinClassCode(segment)}Name`].key,
                    Dictionary[`airClass${getCabinClassCode(segment)}Name`].defaultValue,
                  )}
              </p>
            </div>
            {segment.layoverMins > 0 && (
              <div className={classesExpandCard.expandedLayover}>
                <Divider classes={{ root: classesExpandCard.expandedSeparator }} />
                <div className={classesExpandCard.expandedLayoverDuration}>
                  <span>
                    {TimeDuration(segment.layoverMins)}{' '}
                    {renderContent(
                      Dictionary.airLayoverLabel.key,
                      Dictionary.airLayoverLabel.defaultValue,
                    )}
                  </span>
                  &nbsp;•&nbsp;
                  {`${segment.arrival.airport.city} (${segment.arrival.airport.code})`}
                  {flight.flightSegments[segmentIndex + 1] &&
                    layoverFormats.isOvernightLayover({
                      departure: flight.flightSegments[segmentIndex + 1].departure.date,
                      arrival: segment.arrival.date,
                    }) && (
                      <Chip
                        className={classesExpandCard.overnightLayover}
                        avatar={getOverNightIcon()}
                        label={renderContent(
                          Dictionary.airOvernightLayoverLabel.key,
                          Dictionary.airOvernightLayoverLabel.defaultValue,
                        )}
                        variant="outlined"
                      />
                    )}
                </div>
                <Divider classes={{ root: classesExpandCard.expandedSeparator }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};

FlightDetailsModal.propTypes = {
  openModal: PropTypes.bool,
  handleClose: PropTypes.func,
  flightInfo: PropTypes.shape({
    flight: PropTypes.shape({
      flightIcon: PropTypes.string,
      operatingAirlines: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string,
        }),
      ),
      flightSegments: PropTypes.arrayOf(
        PropTypes.shape({
          marketingAirline: PropTypes.shape({
            icon: PropTypes.string,
          }),
          departure: PropTypes.shape({
            airport: PropTypes.shape({
              name: PropTypes.string,
              code: PropTypes.string,
            }),
            date: PropTypes.string,
          }),
        }),
      ),
    }),
    cabin: PropTypes.shape({
      cabinClass: PropTypes.shape({
        code: PropTypes.string,
      }),
    }),
    flightType: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

export default FlightDetailsModal;
